import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Briefcase, Wallet, Snowflake, CheckCircle2, ArrowRight, Plus } from "lucide-react";
import { api } from "@/lib/api";
import LoadingScreen from "@/components/LoadingScreen";
import StatusPill from "@/components/StatusPill";
import { USER } from "@/constants/testIds";
import { formatDateTime } from "@/lib/utils";
import { useAuth } from "@/context/AuthContext";
import {
  LineChart, Line, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";

function Kpi({ label, value, icon: Icon, tone = "default", testid, hint }) {
  const tones = {
    default: "bg-white",
    primary: "bg-[#0B6936]/5 border-[#0B6936]/20",
    warn: "bg-amber-50 border-amber-200",
  };
  return (
    <div
      data-testid={testid}
      className={`sqit-card p-5 ${tones[tone] || tones.default}`}
    >
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs text-gray-500 uppercase tracking-wider font-semibold">{label}</div>
          <div className="mt-2 font-display text-3xl font-bold text-gray-900">{value}</div>
          {hint && <div className="mt-1 text-xs text-gray-500">{hint}</div>}
        </div>
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-[#0B6936]/10 text-[#0B6936]">
          <Icon size={18} strokeWidth={1.75} />
        </div>
      </div>
    </div>
  );
}

export default function UserDashboardPage() {
  const { user } = useAuth();
  const [summary, setSummary] = useState(null);
  const [balance, setBalance] = useState(null);
  const [jobs, setJobs] = useState(null);
  const [txn, setTxn] = useState([]);

  useEffect(() => {
    Promise.all([
      api.get("/jobs/summary"),
      api.get("/credits/balance"),
      api.get("/jobs?limit=6"),
      api.get("/credits/transactions?limit=30"),
    ]).then(([s, b, j, t]) => {
      setSummary(s.data);
      setBalance(b.data);
      setJobs(j.data);
      setTxn(t.data);
    });
  }, []);

  if (!summary || !balance || !jobs) return <LoadingScreen />;

  const completed = summary.by_status?.completed || 0;
  const displayName = [user?.title, user?.first_name || user?.full_name?.split(" ")[0]]
    .filter(Boolean)
    .join(" ") || user?.full_name?.split(" ")[0] || "there";

  // Chart data: last 14 days credit movement
  const chartData = (() => {
    const days = {};
    const now = new Date();
    for (let i = 13; i >= 0; i--) {
      const d = new Date(now); d.setDate(d.getDate() - i);
      const k = d.toISOString().slice(0, 10);
      days[k] = { date: d.toLocaleDateString(undefined, { month: "short", day: "numeric" }), delta: 0 };
    }
    txn.forEach((t) => {
      const k = (t.created_at || "").slice(0, 10);
      if (days[k]) days[k].delta += Math.abs(t.delta);
    });
    return Object.values(days);
  })();

  return (
    <div data-testid={USER.dashboard} className="space-y-6">
      {/* Welcome */}
      <div className="flex items-center justify-between rounded-xl border border-[#0B6936]/20 bg-[#EAF4E3] px-5 py-4">
        <div>
          <h2 className="font-display text-2xl font-bold text-gray-900">Hello, {displayName}</h2>
          <p className="text-sm text-gray-600 mt-1">Here's what's happening in your workspace today.</p>
        </div>
        <div className="flex gap-2">
          <Link
            to="/app/credits"
            data-testid={USER.buyCreditsBtn}
            className="text-sm bg-[#0B6936] text-white rounded-md px-4 py-2 font-medium hover:bg-[#09582D] transition-colors"
          >
            Buy Credits
          </Link>

          <Link
            to="/app/jobs/new"
            data-testid={USER.newJobBtn}
            className="text-sm bg-[#0B6936] text-white rounded-md px-4 py-2 font-medium inline-flex items-center gap-2 hover:bg-[#09582D] transition-colors"
          >
            <Plus size={14} />
            New Job
          </Link>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi testid={USER.kpiTotalJobs} label="Total Jobs" value={summary.total} icon={Briefcase} />
        <Kpi testid={USER.kpiAvailableCredits} label="Available Credits" value={balance.available} icon={Wallet} tone="primary" hint="Ready to use" />
        <Kpi testid={USER.kpiFrozenCredits} label="Frozen Credits" value={balance.frozen} icon={Snowflake} hint="Reserved for running jobs" />
        <Kpi testid={USER.kpiCompletedJobs} label="Completed" value={completed} icon={CheckCircle2} />
      </div>

      {/* Chart + status breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="sqit-card p-6 lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display font-semibold text-gray-900">Credit activity (14 days)</h3>
            <Link to="/app/credits" className="text-xs font-medium text-[#0B6936] transition-colors hover:text-[#07522f]">View all →</Link>
          </div>
          <div className="h-52">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7EB" />
                <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#6B7280" }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: "#6B7280" }} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #E5E7EB", fontSize: 12 }} />
                <Line
                  type="monotone"
                  dataKey="delta"
                  stroke="#0B6936"
                  strokeWidth={2}
                  dot={{ r: 3, fill: "#0B6936" }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="sqit-card p-6">
          <h3 className="font-display font-semibold text-gray-900 mb-4">Jobs by status</h3>
          <div className="space-y-3">
            {Object.entries(summary.by_status || {}).length === 0 && (
              <div className="text-sm text-gray-500">No jobs yet — submit your first one!</div>
            )}
            {Object.entries(summary.by_status || {}).map(([k, v]) => (
              <div key={k} className="flex items-center justify-between">
                <StatusPill status={k} />
                <span className="text-sm font-semibold text-gray-900">{v}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Recent jobs */}
      <div className="sqit-card overflow-hidden">
        <div className="flex items-center justify-between p-6 pb-4">
          <h3 className="font-display font-semibold text-gray-900">Recent jobs</h3>
          <Link to="/app/jobs" className="inline-flex items-center gap-1 text-xs font-medium text-[#0B6936] transition-colors hover:text-[#07522f]">
            View all <ArrowRight size={12} />
          </Link>
        </div>
        {jobs.length === 0 ? (
          <div className="text-center py-14 border-t border-[#E5E7EB]">
            <p className="text-gray-500">You haven't submitted any jobs yet.</p>
            <Link to="/app/jobs/new" className="mt-4 inline-block rounded-md bg-[#0B6936] px-4 py-2 text-sm font-semibold text-white transition-all duration-200 hover:bg-[#07522f]">
              Submit your first job
            </Link>
          </div>
        ) : (
          <table className="w-full text-sm" data-testid={USER.jobsTable}>
            <thead className="bg-gray-50 border-y border-[#E5E7EB]">
              <tr>
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Job</th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Module</th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Samples</th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Submitted</th>
              </tr>
            </thead>
            <tbody>
              {jobs.map((j) => (
                <tr key={j.id} data-testid={USER.jobRow(j.id)} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                  <td className="px-6 py-4">
                    <Link to={`/app/jobs/${j.id}`} className="font-semibold text-gray-900 transition-colors hover:text-[#0B6936]">
                      {j.job_code}
                    </Link>
                    <div className="text-xs text-gray-500">{j.project_name}</div>
                  </td>
                  <td className="px-6 py-4 text-gray-700">{j.module_name}</td>
                  <td className="px-6 py-4 text-gray-700">{j.number_of_samples}</td>
                  <td className="px-6 py-4"><StatusPill status={j.status} /></td>
                  <td className="px-6 py-4 text-gray-500 text-xs">{formatDateTime(j.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
